import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { fmt, todayMonth, todayISO } from '../lib/format';
import { Receipt, Plus, Trash } from '@phosphor-icons/react';

const CATEGORIES = ['员工工资', 'IT / 软件', '租金', '通讯 / 网络', '团队餐饮', '其他'];

export default function Expenses() {
  const [month, setMonth] = useState(todayMonth());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [employees, setEmployees] = useState([]);

  const load = async () => {
    setLoading(true);
    const [exp, emp] = await Promise.all([
      api.get('/expenses', { params: { month } }),
      api.get('/employees'),
    ]);
    setItems(exp.data);
    setEmployees(emp.data.filter(x => x.role === 'employee' || x.role === 'manager'));
    setLoading(false);
  };
  useEffect(() => { load(); }, [month]);

  const total = items.reduce((s, i) => s + (i.amount || 0), 0);
  const byCategory = {};
  items.forEach(i => { byCategory[i.category] = (byCategory[i.category] || 0) + i.amount; });

  const handleDelete = async (id) => {
    if (!confirm('确认删除?')) return; // eslint-disable-line
    await api.delete(`/expenses/${id}`); load();
  };

  return (
    <div className="space-y-5 fade-in" data-testid="expenses-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-white tracking-tight">开销 / 支出</h1>
          <p className="text-sm text-slate-400 mt-0.5">工资、IT、租金等</p>
        </div>
        <div className="flex gap-2">
          <input type="month" className="input-base font-num max-w-[160px]"
                 value={month} onChange={e => setMonth(e.target.value)} data-testid="expense-month" />
          <button onClick={() => setCreating(true)} className="btn-primary text-sm inline-flex items-center gap-2" data-testid="add-expense-btn">
            <Plus size={16} /> 新增
          </button>
        </div>
      </div>

      <div className="card-base p-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <div className="label-tiny mb-1">本月开销合计</div>
            <div className="font-num text-2xl font-semibold text-rose-400">RM {fmt(total)}</div>
          </div>
          {Object.entries(byCategory).slice(0, 3).map(([cat, amt]) => (
            <div key={cat}>
              <div className="label-tiny mb-1">{cat}</div>
              <div className="font-num text-lg text-white">RM {fmt(amt)}</div>
            </div>
          ))}
        </div>
      </div>

      {loading && <div className="card-base p-12 text-center text-slate-500">加载中…</div>}

      {!loading && items.length === 0 && (
        <div className="card-base p-12 text-center">
          <Receipt size={40} weight="duotone" className="text-slate-700 mx-auto mb-3" />
          <p className="text-slate-500">本月没有开销记录</p>
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="card-base overflow-hidden">
          <table className="w-full hidden md:table">
            <thead className="bg-[#1F2B45] text-xs uppercase text-slate-400 font-semibold tracking-wider">
              <tr>
                <th className="px-4 py-3 text-left">日期</th>
                <th className="px-4 py-3 text-left">类型</th>
                <th className="px-4 py-3 text-left">员工</th>
                <th className="px-4 py-3 text-right">金额 (MYR)</th>
                <th className="px-4 py-3 text-left">备注</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(i => (
                <tr key={i.id} className="border-b border-slate-700/50 hover:bg-slate-800/30">
                  <td className="px-4 py-3 text-sm text-slate-300 font-num">{i.date}</td>
                  <td className="px-4 py-3 text-sm text-slate-200">{i.category}</td>
                  <td className="px-4 py-3 text-sm text-slate-400">{i.employee_name || '-'}</td>
                  <td className="px-4 py-3 text-sm text-right font-num text-rose-300">{fmt(i.amount)}</td>
                  <td className="px-4 py-3 text-sm text-slate-500">{i.notes || '-'}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => handleDelete(i.id)} className="text-rose-400 hover:text-rose-300 p-1">
                      <Trash size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Mobile cards */}
          <div className="md:hidden divide-y divide-slate-800">
            {items.map(i => (
              <div key={i.id} className="p-4">
                <div className="flex justify-between items-start mb-1">
                  <div>
                    <div className="text-sm text-white">{i.category}{i.employee_name ? ` · ${i.employee_name}` : ''}</div>
                    <div className="text-xs text-slate-500 font-num">{i.date}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-num text-rose-300 font-semibold">{fmt(i.amount)}</span>
                    <button onClick={() => handleDelete(i.id)} className="text-rose-400 p-1"><Trash size={14}/></button>
                  </div>
                </div>
                {i.notes && <div className="text-xs text-slate-500">{i.notes}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {creating && <CreateExpenseDialog month={month} employees={employees}
                                        onClose={() => setCreating(false)} onDone={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function CreateExpenseDialog({ month, employees, onClose, onDone }) {
  const [date, setDate] = useState(todayISO());
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [employeeId, setEmployeeId] = useState('');
  const [amount, setAmount] = useState('');
  const [notes, setNotes] = useState('');
  const [monthAttr, setMonthAttr] = useState(month);

  useEffect(() => {
    if (category === '员工工资' && employees.length > 0 && !employeeId) {
      setEmployeeId(employees[0].id);
      setAmount(employees[0].monthly_salary || '');
    }
  }, [category]); // eslint-disable-line

  const handleEmpChange = (id) => {
    setEmployeeId(id);
    const emp = employees.find(e => e.id === id);
    if (emp && category === '员工工资') setAmount(emp.monthly_salary || '');
  };

  const submit = async () => {
    await api.post('/expenses', {
      date, category,
      employee_id: category === '员工工资' ? employeeId : null,
      amount: parseFloat(amount),
      month_attribution: monthAttr,
      notes,
    });
    onDone();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="card-base p-6 max-w-md w-full">
        <h3 className="text-lg font-semibold text-white mb-4">新增开销</h3>
        <div className="space-y-3">
          <div>
            <label className="label-tiny block mb-1">日期</label>
            <input type="date" className="input-base font-num" value={date} onChange={e => setDate(e.target.value)} />
          </div>
          <div>
            <label className="label-tiny block mb-1">归属月份</label>
            <input type="month" className="input-base font-num" value={monthAttr} onChange={e => setMonthAttr(e.target.value)} />
          </div>
          <div>
            <label className="label-tiny block mb-1">类型</label>
            <select className="input-base" value={category} onChange={e => setCategory(e.target.value)}>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          {category === '员工工资' && (
            <div>
              <label className="label-tiny block mb-1">员工</label>
              <select className="input-base" value={employeeId} onChange={e => handleEmpChange(e.target.value)}>
                <option value="">选择员工</option>
                {employees.map(e => <option key={e.id} value={e.id}>{e.name} (月薪 RM {e.monthly_salary || 0})</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="label-tiny block mb-1">金额 (MYR)</label>
            <input type="number" step="0.01" className="input-base font-num" value={amount} onChange={e => setAmount(e.target.value)} />
          </div>
          <div>
            <label className="label-tiny block mb-1">备注</label>
            <input type="text" className="input-base" value={notes} onChange={e => setNotes(e.target.value)} />
          </div>
          <div className="flex gap-3 pt-2">
            <button onClick={onClose} className="btn-secondary flex-1">取消</button>
            <button onClick={submit} className="btn-primary flex-1" data-testid="save-expense">保存</button>
          </div>
        </div>
      </div>
    </div>
  );
}
